import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { addPreviewWatermarkPdfLib } from "./previewWatermark";
import { getFieldPositions, getTemplateUrl } from "./w9Generator";
import { resolveTaxLayout } from "./taxLayouts";
import { loadPdfJs } from "./pdfEngines";

// Set up pdf.js worker using unpkg CDN with correct version

// Convert PDF bytes to image using pdf.js
async function convertPdfToImage(pdfBytes) {
  const loadingTask = (await loadPdfJs()).getDocument({ data: pdfBytes });
  const pdf = await loadingTask.promise;
  
  // Get the first page
  const page = await pdf.getPage(1);
  
  // Set scale for good quality preview
  const scale = 2;
  const viewport = page.getViewport({ scale });
  
  // Create a canvas to render the PDF page
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  
  // Render the page to canvas
  await page.render({
    canvasContext: context,
    viewport: viewport
  }).promise;
  
  // Convert canvas to image data URL
  const imageDataUrl = canvas.toDataURL('image/png', 0.9);
  
  return imageDataUrl;
}

// Get template URL based on tax year

// Field positions for W-9 form (2018/2021-2023 revision)
// Page height: 792, y from bottom

// Field positions for W-9 form (2024 revision)
// Page height: 792, y from bottom

// Get field positions based on tax year

// Generate W-9 preview with watermark
export const generateW9Preview = async (formData, taxYear) => {
  try {
    // Fetch the PDF template
    const layout = await resolveTaxLayout("w9", taxYear, getFieldPositions(taxYear), getTemplateUrl(taxYear));
    const templateResponse = await fetch(layout.templateUrl);
    const templateBytes = await templateResponse.arrayBuffer();
    
    // Load the PDF
    const pdfDoc = await PDFDocument.load(templateBytes);
    const pages = pdfDoc.getPages();
    const page = pages[0];
    const { width, height } = page.getSize();
    
    // Get fonts
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    
    // Get field positions for this year
    const FIELD_POSITIONS = layout.positions;
    
    // Helper function to draw text
    const drawText = (text, position, useBold = false) => {
      if (!text) return;
      const textStr = String(text);
      page.drawText(textStr, {
        x: position.x,
        y: position.y,
        size: position.fontSize || 10,
        font: useBold ? boldFont : font,
        color: rgb(0, 0, 0),
      });
    };
    
    // Helper to draw checkbox mark
    const drawCheckmark = (position) => {
      page.drawText("X", {
        x: position.x,
        y: position.y,
        size: position.fontSize || 10,
        font: boldFont,
        color: rgb(0, 0, 0),
      });
    };
    
    // Fill in the form fields
    drawText(formData.name, FIELD_POSITIONS.name, true);
    drawText(formData.businessName, FIELD_POSITIONS.businessName);
    
    // Tax classification. The admin's layout editor sets
    // __showAll to see every box at once while placing them.
    if (formData.__showAll) {
      ["individual", "ccorp", "scorp", "partnership", "trust", "llc", "other"].forEach((k) => drawCheckmark(FIELD_POSITIONS[k]));
      drawText(formData.llcTaxCode, FIELD_POSITIONS.llcCode);
      drawText(formData.otherClassification, FIELD_POSITIONS.otherText);
    } else switch (formData.taxClassification) {
      case "individual":
        drawCheckmark(FIELD_POSITIONS.individual);
        break;
      case "ccorp":
        drawCheckmark(FIELD_POSITIONS.ccorp);
        break;
      case "scorp":
        drawCheckmark(FIELD_POSITIONS.scorp);
        break;
      case "partnership":
        drawCheckmark(FIELD_POSITIONS.partnership);
        break;
      case "trust":
        drawCheckmark(FIELD_POSITIONS.trust);
        break;
      case "llc":
        drawCheckmark(FIELD_POSITIONS.llc);
        if (formData.llcTaxCode) {
          drawText(formData.llcTaxCode, FIELD_POSITIONS.llcCode);
        }
        break;
      case "other":
        drawCheckmark(FIELD_POSITIONS.other);
        if (formData.otherClassification) {
          drawText(formData.otherClassification, FIELD_POSITIONS.otherText);
        }
        break;
    }
    
    // Exemptions
    drawText(formData.exemptPayeeCode, FIELD_POSITIONS.exemptPayeeCode);
    drawText(formData.fatcaCode, FIELD_POSITIONS.fatcaCode);
    
    // Address
    drawText(formData.address, FIELD_POSITIONS.address);
    
    // City, State, ZIP
    const cityStateZip = [formData.city, formData.state, formData.zipCode].filter(Boolean).join(", ");
    drawText(cityStateZip, FIELD_POSITIONS.cityStateZip);
    
    // Account numbers
    drawText(formData.accountNumbers, FIELD_POSITIONS.accountNumbers);
    
    // TIN - draw each digit individually
    if (formData.__showAll) {
      // both TIN rows, for the layout editor
      [["ssn", formData.ssn], ["ein", formData.ein]].forEach(([k, v]) => {
        String(v || "").replace(/-/g, "").split("").forEach((digit, index) => {
          if (FIELD_POSITIONS[k][index]) drawText(digit, FIELD_POSITIONS[k][index]);
        });
      });
    } else if (formData.tinType === "ssn" && formData.ssn) {
      // SSN format: XXX-XX-XXXX - draw each digit individually
      const ssnDigits = formData.ssn.replace(/-/g, "").split("");
      ssnDigits.forEach((digit, index) => {
        if (FIELD_POSITIONS.ssn[index]) {
          drawText(digit, FIELD_POSITIONS.ssn[index]);
        }
      });
    } else if (formData.tinType === "ein" && formData.ein) {
      // EIN format: XX-XXXXXXX - draw each digit individually
      const einDigits = formData.ein.replace(/-/g, "").split("");
      einDigits.forEach((digit, index) => {
        if (FIELD_POSITIONS.ein[index]) {
          drawText(digit, FIELD_POSITIONS.ein[index]);
        }
      });
    }
    
    // Date
    drawText(formData.signatureDate, FIELD_POSITIONS.signatureDate);
    
    await addPreviewWatermarkPdfLib(pdfDoc, boldFont);
    
    // Save and return as image
    const pdfBytes = await pdfDoc.save();
    
    // Convert PDF to image for preview display
    const imageDataUrl = await convertPdfToImage(pdfBytes);
    
    return imageDataUrl;
    
  } catch (error) {
    console.error("Error generating W-9 preview:", error);
    throw error;
  }
};
